import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { BarChart3, Bell, BookOpen, ChevronLeft, Clock, Database, Download, Headphones, Info, MapPin, Moon, Palette, RefreshCw, RotateCcw, Share, ShieldCheck, Square, Target, Timer, Upload, User, Volume2, Wrench } from 'lucide-react';
import { METHODS, PRAYERS, PRAYER_LABEL, type MethodKey } from '@shared/prayer';
import { PHOTOS, RECITERS, photoOf, reciterById } from '@/data/reciters';
import { useProgress, useSettings } from '@/lib/store';
import type { Accent, ThemePref } from '@/lib/types';
import { WORKER_STALE_MS, disablePush, enablePush, healPush, pushServerStatus, pushState, serverTestNotification, testNotification, type PushLogEntry, type PushState } from '@/lib/push';
import { isIOS, isStandalone } from '@/lib/ios';
import { useSession } from '@/lib/auth';
import { IconButton, Range, RepeatStepper, Row, Section, Segmented, Sheet, Stepper, Switch } from '@/components/ui';
import { MushafPicker, TranslationSelect } from '@/features/quran/ReaderSettingsSheet';
import { MUSHAFS } from '@/features/quran/fonts';
import { ReciterPickerSheet } from '@/features/player/PlayerUI';
import { LocationSheet } from '@/features/prayer/Prayer';
import { clearCache, storageSummary } from '@/features/player/downloads';
import { BACKUP_KEYS, isPersisted, requestPersistence } from '@/lib/persist';
import { APP_BUILD, checkForUpdate, repairApp, useUpdate } from '@/lib/updates';

import { ADHKAR_CREDIT } from '@/features/adhkar/Adhkar';
import { SUNNAH } from '@/features/prayer/sunnah';
import { ProfileSettings } from './Profile';
import { BACKGROUNDS } from '@/components/islamic/Backdrop';
import { canHear } from '@/features/player/access';
import { playAdhan, scheduleTestAdhan, stopAdhan, unlockAdhan, useAdhan } from '@/app/adhan';
import { SkyScene } from '@/components/islamic/Scene';

function Header({ title }: { title: string }) {
  const nav = useNavigate();
  return (
    <div className="flex items-center gap-2 px-3 pb-3">
      <IconButton label="Back" onClick={() => nav(-1)}><ChevronLeft size={20} /></IconButton>
      <h1 className="text-[28px] font-bold">{title}</h1>
    </div>
  );
}

const select = 'max-w-[60%] rounded-lg bg-fill px-2 py-1.5 text-[15px] text-ink';

export function SettingsPage() {
  const { section } = useParams();
  if (section === 'prayer') return <PrayerSettings />;
  if (section === 'profile') return <ProfileSettings />;
  if (section === 'notifications') return <NotificationSettings />;
  if (section === 'quran') return <QuranSettings />;
  if (section === 'storage') return <StorageSettings />;
  if (section === 'about') return <About />;
  return <SettingsRoot />;
}

function SettingsRoot() {
  const s = useSettings((st) => st.settings);
  const update = useSettings((st) => st.update);
  const { data } = useSession();
  return (
    <div className="page-pad">
      <Header title="Settings" />
      <Section>
        <Row icon={<User size={18} />} label="Account & sync" detail={data?.user ? data.user.email : 'Not signed in'} to="/account" />
        <Row icon={<User size={18} />} label="Profile" detail={s.gender === 'female' ? 'Sister' : s.gender === 'male' ? 'Brother' : 'Not set'} to="/settings/profile" />
      </Section>
      <Section title="Appearance">
        <div className="row flex-col !items-stretch gap-2">
          <span className="flex items-center gap-3"><span className="grid h-8 w-8 place-items-center rounded-[9px] bg-fill text-accent"><Palette size={18} /></span>Theme</span>
          <Segmented label="Theme" value={s.theme} onChange={(v: ThemePref) => update({ theme: v })}
            options={[{ value: 'system', label: 'Auto' }, { value: 'light', label: 'Light' }, { value: 'sepia', label: 'Sepia' }, { value: 'dark', label: 'Dark' }]} />
        </div>
        <div className="row flex-col !items-stretch gap-2">
          <span>Accent colour</span>
          <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Accent colour">
            {ACCENTS.map((a) => (
              <button key={a.id} type="button" role="radio" aria-checked={s.accent === a.id} aria-label={a.label} title={a.label} onClick={() => update({ accent: a.id })}
                className={`press grid h-10 w-10 place-items-center rounded-full ${s.accent === a.id ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface' : ''}`} style={{ background: a.swatch }}>
                {s.accent === a.id && <span className="h-3 w-3 rounded-full bg-white/90" />}
              </button>
            ))}
          </div>
        </div>
        <div className="row flex-col !items-stretch">
          <div className="flex justify-between text-[15px]"><span>Glass</span><span className="text-muted">{s.glass < 34 ? 'Clear' : s.glass > 66 ? 'Tinted' : 'Balanced'}</span></div>
          <Range label="Glass transparency" min={0} max={100} value={s.glass} onChange={(v) => update({ glass: v })} />
        </div>
        <div className="row flex-col !items-stretch gap-2">
          <span>Background</span>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Background">
            <button type="button" role="radio" aria-checked={s.background === 'scene'} aria-label="Illustrated sky (follows the prayer day)" onClick={() => update({ background: 'scene' })}
              className={`press relative aspect-[4/3] overflow-hidden rounded-xl ${s.background === 'scene' ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface' : ''}`}>
              <SkyScene phase="dusk" height="100%" />
              <span className="absolute inset-x-0 bottom-0 bg-black/50 px-1 py-0.5 text-[10px] font-semibold text-white">Illustrated sky</span>
            </button>
            {BACKGROUNDS.map((b) => (
              <button key={b.id} type="button" role="radio" aria-checked={s.background === b.id} aria-label={b.label} onClick={() => update({ background: b.id })}
                className={`press relative aspect-[4/3] overflow-hidden rounded-xl bg-fill ${s.background === b.id ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface' : ''}`}>
                <img src={b.thumb} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <span className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-1 py-0.5 text-[10px] font-semibold text-white">{b.label.split(',')[0]}</span>
              </button>
            ))}
          </div>
          <span className="text-[12px] text-muted">Shown behind the Home and Prayer headers.</span>
        </div>
        <Row label="Reciter photos"><Switch label="Reciter photos" checked={s.showReciterPhotos} onChange={(v) => update({ showReciterPhotos: v })} /></Row>
        <Row label="Haptic feedback"><Switch label="Haptics" checked={s.haptics} onChange={(v) => update({ haptics: v })} /></Row>
        <Row label="12-hour clock"><Switch label="12-hour clock" checked={s.hour12} onChange={(v) => update({ hour12: v })} /></Row>
      </Section>
      <Section title="Qur'an">
        <Row icon={<BookOpen size={18} />} label="Mushaf & reading" detail={MUSHAFS.find((m) => m.id === s.mushaf)?.name} to="/settings/quran" />
      </Section>
      <Section title="Prayer & reminders">
        <Row icon={<Clock size={18} />} label="Prayer times" detail={s.prayer.placeName} to="/settings/prayer" />
        <Row icon={<Bell size={18} />} label="Notifications" to="/settings/notifications" />
        <Row icon={<BarChart3 size={18} />} label="Prayer stats & streaks" to="/prayer/stats" />
        <Row icon={<Moon size={18} />} label="Fasting tracker" to="/fasting" />
      </Section>
      <Section title="Goals">
        <Row icon={<Target size={18} />} label="Reading & recitation goals" to="/progress?goal=new" />
      </Section>
      <Section title="Data">
        <Row icon={<Database size={18} />} label="Storage & downloads" to="/settings/storage" />
        <Row icon={<Info size={18} />} label="About, support & credits" to="/settings/about" />
      </Section>
    </div>
  );
}

function QuranSettings() {
  const s = useSettings((st) => st.settings);
  const update = useSettings((st) => st.update);
  const updateDeep = useSettings((st) => st.updateDeep);
  const [pick, setPick] = useState(false);
  return (
    <div className="page-pad">
      <Header title="Qur'an" />
      <div className="px-4 pb-5">
        <Segmented label="Default reading mode" value={s.readingMode} onChange={(v) => update({ readingMode: v })} options={[{ value: 'page', label: 'Mushaf page' }, { value: 'list', label: 'Āyah by āyah' }]} />
      </div>
      <MushafPicker />
      <Section title="Translation">
        <Row label="Show translation"><Switch label="Show translation" checked={s.showTranslation} onChange={(v) => update({ showTranslation: v })} /></Row>
        <Row label="Translator"><TranslationSelect /></Row>
        <Row label="Transliteration"><Switch label="Transliteration" checked={s.showTransliteration} onChange={(v) => update({ showTransliteration: v })} /></Row>
        <Row label="Word-by-word"><Switch label="Word by word" checked={s.wordByWord} onChange={(v) => update({ wordByWord: v })} /></Row>
        <div className="row flex-col !items-stretch">
          <div className="flex justify-between text-[15px]"><span>Arabic size</span><span className="text-muted">{Math.round(s.arabicSize * 100)}%</span></div>
          <Range label="Arabic size" min={0.7} max={2} step={0.05} value={s.arabicSize} onChange={(v) => update({ arabicSize: v })} />
        </div>
      </Section>
      <Section title="Audio" footer="Playback is straight through by default. Recite mode (the mic button in the player) uses the pause and repeat settings above, and can be switched off at any time.">
        <Row icon={<Headphones size={18} />} label="Default reciter" detail={reciterById(s.reciterId).name} onClick={() => setPick(true)} />
        <Row label="Follow the reciter"><Switch label="Follow audio" checked={s.followAudio} onChange={(v) => update({ followAudio: v })} /></Row>
        <Row label="Recite mode: repeat each āyah"><RepeatStepper label="āyah repeats" value={s.reciteRepeat.ayahRepeat} onChange={(v) => updateDeep('reciteRepeat', { ayahRepeat: v })} /></Row>
        <Row label="Recite mode: repeat surah / range"><RepeatStepper label="range repeats" value={s.reciteRepeat.rangeRepeat} onChange={(v) => updateDeep('reciteRepeat', { rangeRepeat: v })} /></Row>
        <div className="row flex-col !items-stretch gap-2">
          <span>Recite mode: pause after each āyah</span>
          <Segmented label="Gap" value={s.reciteRepeat.gapMode} onChange={(v) => updateDeep('reciteRepeat', { gapMode: v })}
            options={[{ value: 'off', label: 'Off' }, { value: 'x1', label: '1×' }, { value: 'x1.5', label: '1.5×' }, { value: 'x2', label: '2×' }, { value: 'fixed', label: 'Fixed' }]} />
        </div>
        <div className="row flex-col !items-stretch">
          <div className="flex justify-between text-[15px]"><span>Speed</span><span className="text-muted">{s.repeat.rate.toFixed(2)}×</span></div>
          <Range label="Speed" min={0.5} max={1.75} step={0.05} value={s.repeat.rate} onChange={(v) => updateDeep('repeat', { rate: v })} />
        </div>
      </Section>
      <Section title="Daily target" footer="Used for the ring when you haven't set a goal.">
        <Row label="Minutes per day"><Stepper value={s.dailyGoalMinutes} min={5} max={240} step={5} onChange={(v) => update({ dailyGoalMinutes: v })} /></Row>
      </Section>
      <ReciterPickerSheet open={pick} onClose={() => setPick(false)} onPick={(id) => { update({ reciterId: id }); setPick(false); }} />
    </div>
  );
}

function PrayerSettings() {
  const { prayer, hijriOffset, trackSunnah } = useSettings((st) => st.settings);
  const update = useSettings((st) => st.update);
  const updateDeep = useSettings((st) => st.updateDeep);
  const [loc, setLoc] = useState(false);
  return (
    <div className="page-pad">
      <Header title="Prayer times" />
      <Section>
        <Row icon={<MapPin size={18} />} label="Location" detail={prayer.placeName} onClick={() => setLoc(true)} />
        <Row label="Time zone" detail={prayer.tz} />
      </Section>
      <Section title="Calculation" footer="Ask your local masjid which method they follow. In the UK, Moonsighting Committee is common; in North America, ISNA.">
        <Row label="Method">
          <select aria-label="Calculation method" className={select} value={prayer.method} onChange={(e) => updateDeep('prayer', { method: e.target.value as MethodKey })}>
            {Object.entries(METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Row>
        <div className="row flex-col !items-stretch gap-2">
          <span>Asr</span>
          <Segmented label="Asr madhab" value={prayer.madhab} onChange={(v) => updateDeep('prayer', { madhab: v })} options={[{ value: 'shafi', label: "Standard (Shāfiʿī, Mālikī, Ḥanbalī)" }, { value: 'hanafi', label: 'Ḥanafī' }]} />
        </div>
        <Row label="High latitudes">
          <select aria-label="High latitude rule" className={select} value={prayer.highLat} onChange={(e) => updateDeep('prayer', { highLat: e.target.value as typeof prayer.highLat })}>
            <option value="auto">Automatic</option>
            <option value="middleofthenight">Middle of the night</option>
            <option value="seventhofthenight">One-seventh of the night</option>
            <option value="twilightangle">Twilight angle</option>
          </select>
        </Row>
      </Section>
      <Section title="Adjustments (minutes)" footer="Fine-tune to match your masjid's timetable.">
        {PRAYERS.map((k) => (
          <Row key={k} label={PRAYER_LABEL[k].en}>
            <Stepper value={prayer.adjust[k] ?? 0} min={-60} max={60} onChange={(v) => updateDeep('prayer', { adjust: { ...prayer.adjust, [k]: v } })} format={(v) => (v > 0 ? `+${v}` : String(v))} />
          </Row>
        ))}
      </Section>
      <Section title="Sunnah prayers to track" footer="These appear under each prayer so you can log them, and in your stats and streaks.">
        {SUNNAH.map((s) => (
          <Row key={s.key} label={<span><span className="block">{s.name}</span><span className="block text-[12px] text-muted">{s.options ? `${s.rakats} rakʿahs by default` : `${s.rakats} rakʿahs`}{s.rawatib ? ' · rawātib' : ''}</span></span>}>
            <Switch label={`Track ${s.name}`} checked={trackSunnah[s.key]} onChange={(v) => updateDeep('trackSunnah', { [s.key]: v })} />
          </Row>
        ))}
      </Section>
      <Section title="Hijri date">
        <Row label="Adjust days"><Stepper value={hijriOffset} min={-2} max={2} onChange={(v) => update({ hijriOffset: v })} format={(v) => (v > 0 ? `+${v}` : String(v))} /></Row>
      </Section>
      <LocationSheet open={loc} onClose={() => setLoc(false)} />
    </div>
  );
}

/* Like the notification tests: hear it now, and prove the prayer-time path
   (scheduled in the app, no tap) works on this phone. */
/* The last day's notifications as the server sent them, and whether this phone
   confirmed showing each one — so a failure is visible, not silent. */
function deliveryLabel(e: PushLogEntry, now: number): { text: string; tone: 'ok' | 'warn' | 'bad' } {
  if (e.status === -1) return { text: 'Not sent — too late (the phone wasn’t reachable)', tone: 'bad' };
  if (e.status === 404 || e.status === 410) return { text: 'Rejected — iPhone had ended the connection', tone: 'bad' };
  if (e.status === 401 || e.status === 403) return { text: 'Rejected — server key problem', tone: 'bad' };
  if (e.status < 200 || e.status >= 300) return { text: e.status ? `Push service error ${e.status} — retried` : 'Network error — retried', tone: 'warn' };
  if (e.receivedAt) return { text: `Shown on this phone ${new Date(e.receivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, tone: 'ok' };
  return now - e.sentAt > 5 * 60_000 ? { text: 'Sent, but this phone didn’t confirm it (Focus, Low Power or connection)', tone: 'warn' } : { text: 'Sent', tone: 'ok' };
}

function RecentDeliveries({ log }: { log: PushLogEntry[] }) {
  const now = Date.now();
  if (!log.length) return null;
  const problems = log.filter((e) => deliveryLabel(e, now).tone !== 'ok').length;
  return (
    <Section title="Recent notifications" footer={problems ? `${problems} of the last ${log.length} didn’t arrive cleanly. If many say “didn’t confirm”, check Focus / Do Not Disturb and Low Power Mode.` : 'Every notification in the last day reached this phone.'}>
      {log.map((e) => {
        const l = deliveryLabel(e, now);
        return (
          <div key={`${e.tag}-${e.sentAt}`} className="row !items-start gap-3">
            <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${l.tone === 'ok' ? 'bg-accent' : l.tone === 'warn' ? 'bg-[#e0a800]' : 'bg-danger'}`} aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px]">{e.title}</span>
              <span className="block text-[12px] text-muted">{new Date(e.fireAt).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })} · {l.text}</span>
            </span>
          </div>
        );
      })}
    </Section>
  );
}

function AdhanTests() {
  const playing = useAdhan((s) => s.playing);
  const [test, setTest] = useState<{ state: 'idle' | 'waiting' | 'played' | 'blocked'; left: number }>({ state: 'idle', left: 0 });
  useEffect(() => {
    if (test.state !== 'waiting' || test.left <= 0) return;
    const t = setTimeout(() => setTest((x) => (x.state === 'waiting' ? { ...x, left: x.left - 1 } : x)), 1000);
    return () => clearTimeout(t);
  }, [test]);
  return (
    <>
    <LockedAdhan />
    <Section title="Test the adhan" footer={test.state === 'blocked' ? 'It couldn’t play — iPhone only allows sound after you’ve tapped in the app since opening it. Tap anywhere, then try again.' : 'The 30-second test uses exactly what happens at prayer time. Start it, then leave the phone — or lock it, if “Adhan when locked” is on.'}>
      {playing ? (
        <Row icon={<Square size={16} />} label="Stop adhan" onClick={() => stopAdhan()} />
      ) : (
        <Row icon={<Volume2 size={18} />} label="Play adhan now" onClick={() => { unlockAdhan(); void playAdhan('preview'); }} />
      )}
      <Row icon={<Timer size={18} />} label="Test the adhan in 30 seconds"
        detail={test.state === 'waiting' ? `in ${test.left}s` : test.state === 'played' ? 'Played ✓' : test.state === 'blocked' ? 'Couldn’t play' : undefined}
        onClick={() => {
          unlockAdhan();
          setTest({ state: 'waiting', left: 30 });
          scheduleTestAdhan(30, (ok) => setTest({ state: ok ? 'played' : 'blocked', left: 0 }));
        }} />
    </Section>
    </>
  );
}

function LockedAdhan() {
  const on = useSettings((s) => s.settings.adhanWhenLocked);
  const update = useSettings((s) => s.update);
  return (
    <Section title="Adhan when the phone is locked" footer="Lock the phone with the app open and the adhan still plays: iPhone keeps a web app running only while it plays audio, so the app plays silence until prayer time (you’ll see “Adhan ready” on the lock screen). Because iPhone plays one app’s audio at a time, this pauses music or podcasts from other apps. Swiping the app away stops it — only a native app can play the adhan then.">
      <Row label="Adhan when locked"><Switch label="Adhan when locked" checked={on} onChange={(v) => { unlockAdhan(); update({ adhanWhenLocked: v }); }} /></Row>
    </Section>
  );
}

function NotificationSettings() {
  const n = useSettings((st) => st.settings.notify);
  const adhanSound = useSettings((st) => st.settings.adhanSound);
  const update = useSettings((st) => st.update);
  const updateDeep = useSettings((st) => st.updateDeep);
  const [state, setState] = useState<PushState>(() => pushState());
  const [server, setServer] = useState<Awaited<ReturnType<typeof pushServerStatus>>>(null);
  const [installHelp, setInstallHelp] = useState(false);

  const [testMsg, setTestMsg] = useState<string | null>(null);
  // Opening this screen always re-checks (and repairs) the server side.
  useEffect(() => { if (state === 'granted') void healPush(true).then((s) => setServer(s ?? null)); }, [state]);
  const workerStale = !!server?.registered && (!server.workerAt || Date.now() - server.workerAt > WORKER_STALE_MS);
  const footer = state !== 'granted' ? undefined
    : !server ? 'Checking the notification server…'
    : !server.registered ? 'Not registered with the server yet — tap Reconnect.'
    : server.dead ? 'The push service rejected this device — tap Reconnect.'
    : server.workerError ? `The notification server reported a problem: ${server.workerError}`
    : workerStale ? `Warning: the notification server hasn't run since ${server.workerAt ? new Date(server.workerAt).toLocaleString() : 'it was set up'}. Notifications won't arrive while the app is closed until it is running again.`
    : `Connected · server checked ${new Date(server.workerAt!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}${server.nextDue ? ` · next due ${new Date(server.nextDue).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}`;

  const enable = async () => {
    if (state === 'needs-install') { setInstallHelp(true); return; }
    setState(await enablePush());
  };

  return (
    <div className="page-pad">
      <Header title="Notifications" />
      <Section footer={footer}>
        {state === 'granted' ? (
          <>
            <Row icon={<ShieldCheck size={18} />} label="Notifications are on" />
            <Row label="Test from the server" detail={testMsg ?? 'lock your phone, arrives within a minute'} onClick={async () => setTestMsg((await serverTestNotification()) ? 'Sent — lock your phone now' : 'Could not reach the server')} />
            <Row label="Test on this device" onClick={() => void testNotification()} />
            <Row label="Reconnect" onClick={async () => { await disablePush(); setState(await enablePush()); setServer(await healPush(true)); }} />
            <Row label="Turn off on this device" danger onClick={async () => { await disablePush(); setState('default'); }} />
          </>
        ) : state === 'denied' ? (
          <Row label={isIOS() ? 'Blocked — enable in iOS Settings → Notifications → For The Ummah' : 'Blocked — allow notifications for this site in your browser settings'} />
        ) : state === 'unsupported' ? (
          <Row label="This browser doesn't support notifications" />
        ) : (
          <Row icon={<Bell size={18} />} label={state === 'needs-install' ? 'Add to Home Screen to enable notifications' : 'Enable notifications'} onClick={enable} />
        )}
      </Section>
      {state === 'granted' && server?.log && <RecentDeliveries log={server.log} />}

      <Section title="Adhan" footer="“Running late” comes 60% of the way through a prayer’s best time (the green on the timer) if you haven’t marked it as prayed. With the app open, the adhan plays at each prayer you’ve switched on. When it can’t (no signal, or the Qurʾān is playing), a short chime marks prayer time instead, softly over the recitation without stopping it. With the app closed, iPhone plays its standard notification sound — web apps can’t use a custom sound there (that needs a native app). Make sure Sounds are on in iPhone Settings → Notifications → For The Ummah.">
        {(['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const).map((k) => (
          <Row key={k} label={PRAYER_LABEL[k].en}><Switch label={`${k} adhan`} checked={!!n.adhan[k]} onChange={(v) => updateDeep('notify', { adhan: { ...n.adhan, [k]: v } })} /></Row>
        ))}
        <Row label="Remind me if I’m running late"><Switch label="Running late reminder" checked={n.late !== false} onChange={(v) => updateDeep('notify', { late: v })} /></Row>
        <Row label="Heads-up before prayer"><Stepper value={n.preReminderMin} min={0} max={60} step={5} onChange={(v) => updateDeep('notify', { preReminderMin: v })} format={(v) => (v ? `${v}m` : 'Off')} /></Row>
        <Row label="Adhan sound (app open)">
          <select aria-label="Adhan sound" className={select} value={adhanSound} onChange={(e) => update({ adhanSound: e.target.value as typeof adhanSound })}>
            <option value="makkah">Makkah</option>
            <option value="madinah">Madinah</option>
            <option value="chime">Short chime</option>
            <option value="none">Notification only</option>
          </select>
        </Row>
        <Row label="Sunrise (end of Fajr)"><Switch label="Sunrise" checked={n.sunrise} onChange={(v) => updateDeep('notify', { sunrise: v })} /></Row>
      </Section>
      <AdhanTests />

      <Section title="Forbidden prayer times" footer="Voluntary prayer is not prayed at sunrise until the sun is up (about 15 min), at the zenith just before Dhuhr (about 10 min), and as the sun sets until Maghrib (about 15 min) — ʿUqbah ibn ʿĀmir, Muslim 831. A missed obligatory prayer may still be made up.">
        <Row label="Notify when they begin"><Switch label="Forbidden prayer times" checked={!!n.forbidden} onChange={(v) => updateDeep('notify', { forbidden: v })} /></Row>
      </Section>

      <Section title="Fasting">
        <Row label="Evening before a sunnah fast"><Switch label="Fast reminder" checked={!!n.fastEve} onChange={(v) => updateDeep('notify', { fastEve: v })} /></Row>
        <Row label="Suḥūr & ifṭār in Ramadan"><Switch label="Ramadan reminders" checked={n.ramadan !== false} onChange={(v) => updateDeep('notify', { ramadan: v })} /></Row>
      </Section>

      <Section title="Adhkar">
        <Row label="Morning adhkar"><Switch label="Morning adhkar" checked={n.morningAdhkar.on} onChange={(v) => updateDeep('notify', { morningAdhkar: { ...n.morningAdhkar, on: v } })} /></Row>
        {n.morningAdhkar.on && <Row label="Minutes after Fajr"><Stepper value={n.morningAdhkar.afterFajrMin} min={0} max={120} step={5} onChange={(v) => updateDeep('notify', { morningAdhkar: { ...n.morningAdhkar, afterFajrMin: v } })} /></Row>}
        <Row label="Evening adhkar"><Switch label="Evening adhkar" checked={n.eveningAdhkar.on} onChange={(v) => updateDeep('notify', { eveningAdhkar: { ...n.eveningAdhkar, on: v } })} /></Row>
        {n.eveningAdhkar.on && <Row label="Minutes after ʿAsr"><Stepper value={n.eveningAdhkar.afterAsrMin} min={0} max={180} step={5} onChange={(v) => updateDeep('notify', { eveningAdhkar: { ...n.eveningAdhkar, afterAsrMin: v } })} /></Row>}
      </Section>

      <Section title="Qur'an">
        <Row label="Daily goal reminder"><Switch label="Goal reminder" checked={n.dailyGoal.on} onChange={(v) => updateDeep('notify', { dailyGoal: { ...n.dailyGoal, on: v } })} /></Row>
        {n.dailyGoal.on && <Row label="Remind me at"><input type="time" aria-label="Reminder time" value={n.dailyGoal.time} onChange={(e) => updateDeep('notify', { dailyGoal: { ...n.dailyGoal, time: e.target.value } })} className="rounded-lg bg-fill px-2 py-1 text-ink" /></Row>}
        <Row label="Surah al-Kahf on Fridays"><Switch label="Kahf Friday" checked={n.kahfFriday} onChange={(v) => updateDeep('notify', { kahfFriday: v })} /></Row>
        <Row label="Ḍuḥā (30 min after sunrise)"><Switch label="Duha reminder" checked={!!n.duha} onChange={(v) => updateDeep('notify', { duha: v })} /></Row>
        <Row label="Tahajjud (last third of the night)"><Switch label="Tahajjud reminder" checked={n.lastThird} onChange={(v) => updateDeep('notify', { lastThird: v })} /></Row>
      </Section>

      <InstallSheet open={installHelp} onClose={() => setInstallHelp(false)} />
    </div>
  );
}

export function InstallSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Add to Home Screen">
      <div className="space-y-4 px-5 pb-4 text-[16px]">
        <p className="text-muted">On iPhone, prayer and adhkar notifications work once For The Ummah is on your Home Screen. It then opens full-screen like an app.</p>
        <ol className="space-y-3">
          <li className="flex items-center gap-3"><span className="grid h-8 w-8 place-items-center rounded-full bg-fill font-bold">1</span>Tap <Share size={18} className="inline text-accent" /> <b>Share</b> in Safari's toolbar</li>
          <li className="flex items-center gap-3"><span className="grid h-8 w-8 place-items-center rounded-full bg-fill font-bold">2</span>Choose <b>Add to Home Screen</b></li>
          <li className="flex items-center gap-3"><span className="grid h-8 w-8 place-items-center rounded-full bg-fill font-bold">3</span>Open it from your Home Screen, then turn on notifications</li>
        </ol>
      </div>
    </Sheet>
  );
}

function StorageSettings() {
  const [sum, setSum] = useState<Awaited<ReturnType<typeof storageSummary>> | null>(null);
  const [persisted, setPersisted] = useState<boolean | null>(null);
  const refresh = () => { void storageSummary().then(setSum); void isPersisted().then(setPersisted); };
  useEffect(refresh, []);
  const { data: session } = useSession();
  const mb = (b: number) => `${(b / 1048576).toFixed(b > 1e9 ? 0 : 1)} MB`;
  const exportData = () => {
    const st = useProgress.getState() as unknown as Record<string, unknown>;
    const data = { app: 'fortheummah', version: 2, exportedAt: new Date().toISOString(), settings: useSettings.getState().settings, progress: Object.fromEntries(BACKUP_KEYS.map((k) => [k, st[k]])) };
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: `fortheummah-${new Date().toISOString().slice(0, 10)}.json` });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const importData = (f: File) => f.text().then((t) => {
    const d = JSON.parse(t);
    if (!d || typeof d !== 'object' || (d.app && d.app !== 'fortheummah')) throw new Error('not a backup');
    if (d.progress) useProgress.getState().mergeRemote(d.progress);
    if (d.settings) useSettings.getState().update(d.settings);
    alert('Imported — your data was merged with what is already here.');
  }).catch(() => alert('That file could not be read.'));

  return (
    <div className="page-pad">
      <Header title="Storage" />
      <Section title="Keeping your data safe" footer={persisted ? 'Your browser has agreed not to clear this app’s data.' : isIOS() && !isStandalone() ? 'Safari clears website data after 7 days without a visit. Add the app to your Home Screen, sign in to sync, or export a backup regularly.' : 'Your browser may clear this data if the device runs low on space. Sign in to sync, or export a backup.'}>
        <Row label="Protected from clearing" detail={persisted == null ? 'Unknown' : persisted ? 'Yes' : 'No'}>{!persisted && <button type="button" className="press rounded-full bg-fill px-3 py-1.5 text-[13px] font-semibold" onClick={async () => setPersisted(await requestPersistence())}>Request</button>}</Row>
        <Row label="Cloud backup" detail={session?.user ? 'On — synced to your account' : 'Off — sign in to turn on'} to="/account" />
      </Section>
      <Section footer="Downloaded recitations play offline. Pages you have read stay available offline too.">
        <Row label="Used on this device" detail={sum ? mb(sum.used) : '…'} />
        <Row label="Downloaded recitations" detail={sum?.audioCount ?? '…'} />
        <Row label="Remove downloaded audio" danger onClick={async () => { if (confirm('Remove all downloaded recitations?')) { await clearCache('audio'); refresh(); } }} />
        <Row label="Clear cached pages & fonts" onClick={async () => { await clearCache('data'); await clearCache('fonts'); refresh(); }} />
      </Section>
      <AppVersion />
      <Section title="Your data">
        <Row icon={<Download size={18} />} label="Export (JSON)" onClick={exportData} />
        <label className="row press cursor-pointer">
          <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-fill text-accent"><Upload size={18} /></span>
          <span className="flex-1">Import</span>
          <input type="file" accept="application/json" className="hidden" onChange={(e) => e.target.files?.[0] && importData(e.target.files[0])} />
        </label>
        <Row icon={<RotateCcw size={18} />} label="Reset settings to defaults" danger onClick={() => { if (confirm('Reset all settings?')) useSettings.getState().reset(); }} />
      </Section>
    </div>
  );
}

/* New deploys install themselves (src/lib/updates.ts); these are the manual levers. */
function AppVersion() {
  const { checking, lastChecked } = useUpdate();
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <Section title="App version" footer={msg ?? 'Updates install by themselves when you reopen the app. If something looks out of date, Repair reloads a fresh copy — downloads, cached pages and your data are kept.'}>
      <Row label="Version" detail={APP_BUILD} />
      <Row icon={<RefreshCw size={18} className={checking ? 'animate-spin' : ''} />} label="Check for updates" detail={lastChecked ? `checked ${new Date(lastChecked).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : undefined}
        onClick={async () => { setMsg(null); setMsg((await checkForUpdate()) ? 'A new version was found — reloading…' : 'You have the latest version.'); }} />
      <Row icon={<Wrench size={18} />} label="Repair app (reload fresh copy)" onClick={() => { if (confirm('Reload a fresh copy of the app? Your data and downloads are kept.')) void repairApp(); }} />
    </Section>
  );
}

const ACCENTS: { id: Accent; label: string; swatch: string }[] = [
  { id: 'emerald', label: 'Emerald', swatch: '#1fa774' },
  { id: 'pink', label: 'Pink', swatch: '#e85b9a' },
  { id: 'rose', label: 'Rose', swatch: '#e25d68' },
  { id: 'violet', label: 'Violet', swatch: '#8b6cf0' },
  { id: 'blue', label: 'Blue', swatch: '#3a8be8' },
  { id: 'teal', label: 'Teal', swatch: '#1fb0a8' },
  { id: 'gold', label: 'Gold', swatch: '#c99a3a' },
];

/** Your donation link. The Donate button on the About page appears once this is set. */
const DONATE_URL = '';

function About() {
  const gender = useSettings((st) => st.settings.gender);
  return (
    <div className="page-pad">
      <Header title="About" />
      <div className="px-6 pb-6 text-[15px] leading-relaxed text-muted">
        <p className="font-arabic text-[22px] text-gold" dir="rtl">وَرَتِّلِ ٱلْقُرْءَانَ تَرْتِيلًا</p>
        <p className="mt-1 italic">“…and recite the Qur'an with measured recitation.” — al-Muzzammil 73:4</p>
      </div>
      <Section title="For the ummah">
        <div className="space-y-2 px-4 py-3 text-[15px] leading-relaxed" data-testid="about-free">
          <p>This app is for the ummah, and it is free.</p>
          <p>I don’t believe in charging the ummah to do good. There is nothing to pay for and nothing locked away. This is for you, and for Allah.</p>
        </div>
      </Section>
      <Section title="Supporting the app">
        <div className="space-y-2 px-4 py-3 text-[15px] leading-relaxed" data-testid="about-donate">
          <p>If you would like to donate, give whatever you can. Please feel free. If you can’t, that is completely okay.</p>
          <p>Either way, please remember the <span className="font-semibold">Adeyi family</span>, the <span className="font-semibold">Fadahunsi family</span> and the <span className="font-semibold">Adebanji family</span> in your duʿāʾ.</p>
          <p className="text-muted">Jazākum Allāhu khayran.</p>
        </div>
        {DONATE_URL && (
          <a href={DONATE_URL} target="_blank" rel="noreferrer" className="row press">
            <span className="flex-1 text-[15px] font-semibold text-accent">Donate</span>
          </a>
        )}
      </Section>
      <Section title="Credits" footer="Women reciters and ruqyah recordings streamed from a public archive (apart from the CC0 al-ʿAjmī and Alafasy ruqyah) are pending permission from the rights holders before a public launch.">
        <Row label="Qur'an text, translations, mushaf fonts" detail="Quran Foundation" />
        <Row label="Recitations & timings" detail="QuranicAudio / Quran.com" />
        <Row label="Malaysian qāriʾāt recordings" detail="mp3quran.net" />
        <Row label="Ruqyah recordings" detail="Internet Archive" />
        <Row label="Hj. Maria Ulfah recording" detail="Internet Archive" />
        <Row label="Adhkar" detail={ADHKAR_CREDIT.split(' — ')[0]} />
        <Row label="Prayer calculation" detail="adhan-js (MIT)" />
        <Row label="City search" detail="Open-Meteo" />
      </Section>
      <Section title="Background photos" footer="Freely licensed photographs from Wikimedia Commons.">
        {BACKGROUNDS.map((b) => (
          <a key={b.id} href={b.page} target="_blank" rel="noreferrer" className="row press">
            <span className="min-w-0 flex-1"><span className="block text-[15px]">{b.label}</span><span className="block truncate text-[12px] text-muted">{b.artist}</span></span>
            <span className="text-[13px] text-muted">{b.licence}</span>
          </a>
        ))}
      </Section>
      <Section title="Reciter photos" footer="Freely licensed images from Wikimedia Commons. Reciters without an openly licensed photo are shown with a monogram.">
        {RECITERS.filter((r) => photoOf(r) && PHOTOS[r.id] && canHear(r, gender)).map((r) => (
          <a key={r.id} href={PHOTOS[r.id].page} target="_blank" rel="noreferrer" className="row press">
            <span className="min-w-0 flex-1"><span className="block text-[15px]">{r.name}</span><span className="block truncate text-[12px] text-muted">{PHOTOS[r.id].artist}</span></span>
            <span className="text-[13px] text-muted">{PHOTOS[r.id].licence}</span>
          </a>
        ))}
      </Section>
    </div>
  );
}
